import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PasswordInput } from "@/components/PasswordInput";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NIGERIAN_STATES } from "@/lib/constants";
import { lgasForState } from "@/lib/lga";
import { Loader2, ArrowLeft, HeartHandshake, Store } from "lucide-react";

export default function Signup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const fixedRole = ["vendor", "sponsor"].includes(params.get("role")) ? params.get("role") : null;
  const [role, setRole] = useState(fixedRole || "sponsor");
  const [f, setF] = useState({ name: "", email: "", password: "", org_name: "", org_code: "", phone: "", state: "", lga: "", address: "", landmark: "", bank_account: "", moniepoint_account_number: "", account_name: "", vendor_category: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", { role, ...f });
      login(data.user);
      toast.success("Account created!");
      navigate(role === "sponsor" ? "/sponsor" : "/vendor");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-950 tmf-grain flex flex-col">
      <div className="p-6"><button onClick={() => navigate("/")} className="text-emerald-200 flex items-center gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Home</button></div>
      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl tmf-rise">
          <Logo showTagline />
          <h1 className="mt-6 font-display font-bold text-2xl text-emerald-950">Create a TMF Account</h1>

          {fixedRole ? (
            <div data-testid="signup-fixed-role" className="mt-5 flex items-center gap-3 rounded-xl border-2 border-emerald-500 bg-emerald-50 p-4">
              {fixedRole === "vendor" ? <Store className="w-6 h-6 text-emerald-600" /> : <HeartHandshake className="w-6 h-6 text-emerald-600" />}
              <div className="font-display font-extrabold text-lg tracking-wide text-emerald-950">REGISTER AS A TMF {fixedRole.toUpperCase()}</div>
            </div>
          ) : (
          <div className="mt-5 grid grid-cols-2 gap-3">
            {[
              { v: "sponsor", icon: HeartHandshake, t: "Sponsor" },
              { v: "vendor", icon: Store, t: "Vendor" },
            ].map((o) => (
              <button key={o.v} type="button" data-testid={`signup-role-${o.v}`} onClick={() => setRole(o.v)}
                className={`rounded-xl border-2 p-4 text-left transition-all ${role === o.v ? "border-emerald-500 bg-emerald-50" : "border-gray-200"}`}>
                <o.icon className="w-5 h-5 text-emerald-600" />
                <div className="mt-2 font-semibold text-sm">{o.t}</div>
              </button>
            ))}
          </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Contact Name</Label><Input data-testid="signup-name" value={f.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 h-11" /></div>
              <div><Label>Email</Label><Input data-testid="signup-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required className="mt-1.5 h-11" /></div>
            </div>
            <div><Label>Password</Label><PasswordInput testid="signup-password" value={f.password} onChange={(e) => set("password", e.target.value)} required className="mt-1.5 h-11" /></div>

            {role === "sponsor" ? (
              <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Organisation Name</Label><Input data-testid="signup-org-name" value={f.org_name} onChange={(e) => set("org_name", e.target.value)} placeholder="SPONSORING BODY" required className="mt-1.5 h-11" /></div>
                <div><Label>Programme Code Prefix</Label><Input data-testid="signup-org-code" value={f.org_code} onChange={(e) => set("org_code", e.target.value.toUpperCase())} placeholder="e.g. SPO" required className="mt-1.5 h-11 font-mono" /><p className="mt-1 text-[11px] text-gray-500">First three letters of your Sponsor Name</p></div>
              </div>
              <div><Label>Contact Phone Number <span className="text-red-500">*</span></Label><Input data-testid="signup-sponsor-phone" type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="e.g. 08031234567" required pattern="^\+?[0-9 ]{10,15}$" title="Enter a valid phone number (at least 10 digits)" className="mt-1.5 h-11" /></div>
              </>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Business Name</Label><Input data-testid="signup-org-name" value={f.org_name} onChange={(e) => set("org_name", e.target.value)} required className="mt-1.5 h-11" /></div>
                  <div><Label>Phone</Label><Input data-testid="signup-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-11" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>State</Label>
                    <Select value={f.state} onValueChange={(v) => setF((p) => ({ ...p, state: v, lga: "" }))}>
                      <SelectTrigger data-testid="signup-state" className="mt-1.5 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>LGA</Label>
                    <Select value={f.lga} onValueChange={(v) => set("lga", v)} disabled={!f.state}>
                      <SelectTrigger data-testid="signup-lga" className="mt-1.5 h-11"><SelectValue placeholder={f.state ? "Select LGA" : "Select state first"} /></SelectTrigger>
                      <SelectContent>{lgasForState(f.state).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Address</Label><Input data-testid="signup-address" value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Nearest Landmark</Label><Input data-testid="signup-landmark" value={f.landmark} onChange={(e) => set("landmark", e.target.value)} maxLength={80} placeholder="e.g. Opposite Shoprite, beside First Bank" className="mt-1.5 h-11" /><p className="mt-1 text-[11px] text-gray-500">Helps beneficiaries find your shop.</p></div>
                <div>
                  <Label>Vendor Category</Label>
                  <Select value={f.vendor_category} onValueChange={(v) => set("vendor_category", v)}>
                    <SelectTrigger data-testid="signup-category" className="mt-1.5 h-11"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{["Supermarket", "Food Store", "Open Market Trader", "Restaurant", "Pharmacy", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Vendor Account Name</Label>
                  <Input data-testid="signup-account-name" value={f.account_name} onChange={(e) => set("account_name", e.target.value)} required minLength={3} placeholder="Name on your Moniepoint account" className="mt-1.5 h-11" />
                  <p className="mt-1 text-[11px] text-gray-500">May be different from the Contact Name.</p>
                </div>
                <div>
                  <Label>Moniepoint Account Number (for settlements)</Label>
                  <Input data-testid="signup-moniepoint" value={f.moniepoint_account_number} onChange={(e) => set("moniepoint_account_number", e.target.value.replace(/\D/g, ""))} maxLength={10} minLength={10} pattern="\d{10}" required placeholder="10-digit Moniepoint account" className="mt-1.5 h-11 font-mono" />
                  <p className="mt-1 text-[11px] text-gray-500">Bank: Moniepoint MFB (code 50515)</p>
                  <p className="mt-1 text-[11px] text-amber-700 bg-amber-50 rounded-md px-2 py-1 border border-amber-100">Redemptions are paid directly to this account. TMF will verify it before you can redeem vouchers.</p>
                </div>
              </>
            )}

            <Button data-testid="signup-submit" type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Create ${role === "sponsor" ? "Sponsor" : "Vendor"} Account`}
            </Button>
          </form>
          <p className="mt-5 text-center text-sm text-gray-500">Already have an account? <button onClick={() => navigate("/login")} className="text-emerald-700 font-semibold">Login</button></p>
        </div>
      </div>
    </div>
  );
}
